import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Normaliza una nota de cliente recibida del backend
 */
export const normalizeClientNote = item => {
  if (!item) return null;
  const rawColor = item.noteColor || item.NoteColor || item.vColorNota || item.VColorNota || item.vColor || item.color || '#d1fae5';
  return {
    idClienteNotas: item.idClienteNotas ?? item.IdClienteNotas ?? item.noteId ?? item.NoteId ?? item.id,
    idClienteAkasia: item.idClienteAkasia ?? item.IdClienteAkasia ?? item.customerId ?? item.CustomerId,
    note: item.note || item.Note || item.vNotaCliente || item.VNotaCliente || item.vNota || item.VNota || '',
    noteColor: rawColor,
    createdUser: item.createdUser || item.CreatedUser || item.vCreatedUser || item.VCreatedUser || 'Asesor',
    createdDate: item.createdDate || item.CreatedDate || item.dCreatedDate || item.DCreatedDate || new Date().toISOString(),
    isActive: item.bIsActive ?? item.BIsActive ?? item.isActive ?? true
  };
};

/**
 * Hook para consultar y gestionar las notas de un cliente
 */
export const useClientNotes = ({ customerId, token }) => {
  const [notes, setNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const lastCustomerIdRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    if (!customerId || !token) {
      setNotes([]);
      return [];
    }

    setIsLoadingNotes(true);
    try {
      const response = await fetch(`/api/cliente/GetClientNotes?idClienteAkasia=${encodeURIComponent(customerId)}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        let rawList = [];
        if (Array.isArray(result)) {
          rawList = result;
        } else if (Array.isArray(result?.data)) {
          rawList = result.data;
        } else if (result?.detalles) {
          const parsed = typeof result.detalles === 'string' ? JSON.parse(result.detalles) : result.detalles;
          rawList = parsed?.Table || parsed?.Table1 || (Array.isArray(parsed) ? parsed : []);
        }

        const normalized = rawList
          .map(normalizeClientNote)
          .filter(n => n && n.idClienteNotas && n.isActive !== false);

        // Ordenar de más reciente a más antigua
        normalized.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

        setNotes(normalized);
        return normalized;
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error al cargar notas del cliente:', err);
      setNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
    return [];
  }, [customerId, token]);

  useEffect(() => {
    if (lastCustomerIdRef.current === customerId) return;
    lastCustomerIdRef.current = customerId;
    fetchNotes();
  }, [customerId, fetchNotes]);

  const createNote = useCallback(async ({ note, noteColor = '#d1fae5' }) => {
    if (!customerId || !note?.trim() || !token) {
      throw new Error('La nota no puede estar vacía');
    }

    setIsSavingNote(true);
    try {
      const response = await fetch('/api/cliente/CreateClientNote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          customerId: Number(customerId),
          note: note.trim(),
          noteColor: noteColor || '#d1fae5'
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al crear la nota del cliente');
      }

      await fetchNotes();
      return true;
    } finally {
      setIsSavingNote(false);
    }
  }, [customerId, token, fetchNotes]);

  const updateNote = useCallback(async ({ idClienteNotas, note, noteColor = '#d1fae5' }) => {
    if (!idClienteNotas || !note?.trim() || !token) {
      throw new Error('La nota no puede estar vacía');
    }

    setIsSavingNote(true);
    try {
      const response = await fetch('/api/cliente/UpdateClientNote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          noteId: Number(idClienteNotas),
          customerId: Number(customerId),
          note: note.trim(),
          noteColor: noteColor || '#d1fae5'
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al actualizar la nota del cliente');
      }

      await fetchNotes();
      return true;
    } finally {
      setIsSavingNote(false);
    }
  }, [customerId, token, fetchNotes]);

  const deleteNote = useCallback(async ({ idClienteNotas }) => {
    if (!idClienteNotas || !token) {
      throw new Error('IdClienteNotas es requerido');
    }

    setIsSavingNote(true);
    try {
      const response = await fetch('/api/cliente/DeleteClientNote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          noteId: Number(idClienteNotas)
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al eliminar la nota del cliente');
      }

      await fetchNotes();
      return true;
    } finally {
      setIsSavingNote(false);
    }
  }, [token, fetchNotes]);

  return {
    notes,
    notesCount: notes.length,
    isLoadingNotes,
    isSavingNote,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote
  };
};

export default useClientNotes;
